import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/site";
import { api, fileUrl, formatDate } from "@/lib/api";
import { MaskedLines, FadeIn, Reveal } from "@/components/site/Motion";

const Card = ({ p, big }) => (
  <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`}
    className={`glow-card group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(76,29,149,0.35)] ${big ? "lg:grid lg:grid-cols-12" : ""}`}>
    <div className={`relative overflow-hidden bg-gradient-to-br from-[#1b0f3a] to-[#0a1233] ${big ? "aspect-[16/10] lg:col-span-7 lg:aspect-auto lg:min-h-[420px]" : "aspect-[16/10]"}`}>
      {p.cover_image
        ? <img src={fileUrl(p.cover_image)} alt={p.title} data-no-translate loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        : <div className="grid-lines absolute inset-0" />}
    </div>
    <div className={`flex flex-1 flex-col p-6 sm:p-8 ${big ? "lg:col-span-5 lg:justify-center lg:p-10" : ""}`}>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-violet-700">{formatDate(p.published_at)}</p>
      <h2 data-no-translate className={`mt-3 font-display font-medium leading-tight text-slate-900 ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{p.title}</h2>
      {p.excerpt && <p data-no-translate className="mt-3 line-clamp-3 text-[15px] leading-relaxed text-slate-600">{p.excerpt}</p>}
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-900">Read article <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" /></span>
    </div>
  </Link>
);

export default function Insights() {
  useSeo("Search Insights | SEO & AI Search Blog | Perfect Digitals", "Insights on technical SEO, Google algorithm changes, AI Overviews, ChatGPT Search, AEO, GEO and how customers discover companies online.");
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/posts").then((r) => alive && setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => alive && setPosts([]));
    return () => { alive = false; };
  }, []);
  const [first, ...rest] = posts || [];

  return (
    <div data-testid="insights-page">
      <section className="grain relative overflow-hidden bg-ink pb-20 pt-36 text-white sm:pb-24 sm:pt-44">
        <div aria-hidden="true" className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
        <div aria-hidden="true" className="absolute right-[-10%] top-[-20%] h-[600px] w-[600px] rounded-full bg-violet-700/25 blur-[140px]" />
        <div className="wrap relative">
          <FadeIn className="font-mono text-[11px] uppercase tracking-[0.25em] text-violet-300">Insights</FadeIn>
          <MaskedLines testid="insights-title" lines={["Search", <span className="font-serif-i text-gradient">Insights</span>]} className="mt-6 font-display text-[3rem] font-medium leading-[0.95] sm:text-7xl lg:text-8xl" />
          <FadeIn delay={0.5} className="mt-8 max-w-2xl space-y-4 text-[15px] leading-relaxed text-slate-300 sm:text-base">
            <p>Search is evolving rapidly. Our Insights explore the developments that actually matter to businesses, from technical SEO and Google algorithm changes to AI Overviews, ChatGPT Search, AEO, GEO and the changing way customers discover companies online.</p>
            <p>We also publish original research examining how industries and brands are appearing across AI-powered search.</p>
          </FadeIn>
        </div>
      </section>
      <section className="bg-[#F3F2F8] py-20 sm:py-28">
        <div className="wrap">
          {posts === null && <div className="grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-white" />)}</div>}
          {posts?.length === 0 && (
            <div data-testid="insights-empty" className="rounded-3xl border border-slate-200 bg-white p-12 text-center">
              <p className="font-display text-2xl text-slate-900">New articles are on the way.</p>
              <p className="mt-2 text-slate-600">Check back soon for our latest thinking on SEO and AI search.</p>
            </div>
          )}
          {first && <Reveal><Card p={first} big /></Reveal>}
          {rest.length > 0 && (
            <div data-testid="insights-grid" className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.06}><Card p={p} /></Reveal>)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
